export interface ActivityData {
  number?: string;
  title: string;
  link?: string;
  priority?: string;
  deadline?: string;
  visibility?: string;
  responsibles?: string;
  disciplines?: string;
}

export interface ContentSegment {
  type: 'text' | 'activity';
  text?: string;
  activity?: ActivityData;
}

function parseMetaLine(line: string): { key: string; value: string } | null {
  let cleaned = line.trim().replace(/^[\s•·\-*]+/, '').replace(/\*{1,2}/g, '');
  const match = cleaned.match(
    /^(Descri[çc][aã]o|Status|Prioridade|Prazo(?:\s+original)?|Visibilidade|Respons[aá]veis?|Disciplinas?)\s*:\s*(.*)/i
  );
  if (!match) return null;
  const key = match[1]
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return { key, value: match[2].trim() };
}

function parseTitleLine(line: string): { number?: string; title: string } | null {
  const raw = line.trim().replace(/\*{1,2}/g, '');

  const withNumber = raw.match(/^(?:(?:Atividade|Apontamento|Issue)\s+)?#(\d+)\s*:\s*(.+)/i);
  if (withNumber) return { number: withNumber[1], title: withNumber[2].trim() };

  const withLabel = raw.match(/^(?:Atividade|Apontamento|Issue)\s*:\s*(.+)/i);
  if (withLabel) return { title: withLabel[1].trim() };

  const numbered = raw.match(/^\d+[.)]\s+(.+)/);
  if (numbered) return { title: numbered[1].trim() };

  return null;
}

function parseLinkLine(line: string): { title: string; link: string } | null {
  const cleaned = line.trim().replace(/^[\s•·\-*]+/, '').replace(/\*+$/, '').trim();
  const match = cleaned.match(/^\[(.+?)\]\((.+?)\)\s*$/);
  if (!match) return null;
  return {
    title: match[1].replace(/\*{1,2}/g, '').replace(/^(?:Atividade|Apontamento|Issue)\s*:\s*/i, '').trim(),
    link: match[2],
  };
}

function parseNumberOnlyLine(line: string): string | null {
  const cleaned = line.trim().replace(/\*{1,2}/g, '').replace(/^[\s•·\-*]+/, '').trim();
  const match = cleaned.match(/^#(\d+)\s*$/);
  return match ? match[1] : null;
}

function consumeMeta(
  lines: string[],
  startIndex: number
): {
  priority?: string;
  deadline?: string;
  visibility?: string;
  responsibles?: string;
  disciplines?: string;
  nextIndex: number;
} {
  let priority: string | undefined;
  let deadline: string | undefined;
  let visibility: string | undefined;
  let responsibles: string | undefined;
  let disciplines: string | undefined;
  let k = startIndex;

  while (k < lines.length) {
    const trimmed = lines[k].trim();
    if (trimmed === '') { k++; continue; }

    const meta = parseMetaLine(lines[k]);
    if (!meta) break;

    if (meta.key === 'prioridade') priority = meta.value;
    else if (meta.key.startsWith('prazo')) deadline = meta.value;
    else if (meta.key === 'visibilidade') visibility = meta.value;
    else if (meta.key.startsWith('respons')) responsibles = meta.value;
    else if (meta.key.startsWith('disciplina')) disciplines = meta.value;
    k++;
  }

  return { priority, deadline, visibility, responsibles, disciplines, nextIndex: k };
}

export function parseActivities(content: string): ContentSegment[] {
  const lines = content.split('\n');
  const segments: ContentSegment[] = [];
  const seenNumbers = new Set<string>();
  let currentText = '';
  let i = 0;

  const flush = () => {
    if (currentText.trim()) {
      segments.push({ type: 'text', text: currentText });
      currentText = '';
    }
  };

  const pushActivity = (activity: ActivityData) => {
    if (activity.number) {
      if (seenNumbers.has(activity.number)) return;
      seenNumbers.add(activity.number);
    }
    flush();
    segments.push({ type: 'activity', activity });
  };

  while (i < lines.length) {
    const numberOnly = parseNumberOnlyLine(lines[i]);
    if (numberOnly !== null) {
      let j = i + 1;
      while (j < lines.length && lines[j].trim() === '') j++;

      if (j < lines.length) {
        const linkData = parseLinkLine(lines[j]);
        if (linkData) {
          const {
            priority,
            deadline,
            visibility,
            responsibles,
            disciplines,
            nextIndex
          } = consumeMeta(lines, j + 1);
          pushActivity({
            number: numberOnly,
            title: linkData.title,
            link: linkData.link,
            priority,
            deadline,
            visibility,
            responsibles,
            disciplines
          });
          i = nextIndex;
          continue;
        }
      }
    }

    // Standalone markdown link as activity: [#N: Title](url)
    const standaloneLink = parseLinkLine(lines[i]);
    if (standaloneLink) {
      let j = i + 1;
      while (j < lines.length && lines[j].trim() === '') j++;

      if (j < lines.length && parseMetaLine(lines[j]) !== null) {
        let number: string | undefined;
        let title = standaloneLink.title;
        const numMatch = title.match(/^(?:(?:Atividade|Apontamento|Issue)\s+)?#?(\d+)\s*[:\s]\s*(.+)/i);
        if (numMatch) {
          number = numMatch[1];
          title = numMatch[2].trim();
        }
        const {
          priority,
          deadline,
          visibility,
          responsibles,
          disciplines,
          nextIndex
        } = consumeMeta(lines, j);
        pushActivity({ number, title, link: standaloneLink.link, priority, deadline, visibility, responsibles, disciplines });
        i = nextIndex;
        continue;
      }
    }

    const titleData = parseTitleLine(lines[i]);
    if (titleData) {
      let j = i + 1;
      while (j < lines.length && lines[j].trim() === '') j++;

      // Look ahead for an optional link line before meta
      let activityLink: string | undefined;
      if (j < lines.length && parseMetaLine(lines[j]) === null) {
        const linkCandidate = parseLinkLine(lines[j]);
        if (linkCandidate) {
          activityLink = linkCandidate.link;
          j++;
          while (j < lines.length && lines[j].trim() === '') j++;
        }
      }

      if (j < lines.length && parseMetaLine(lines[j]) !== null) {
        const {
          priority,
          deadline,
          visibility,
          responsibles,
          disciplines,
          nextIndex
        } = consumeMeta(lines, j);
        pushActivity({
          number: titleData.number,
          title: titleData.title,
          link: activityLink,
          priority,
          deadline,
          visibility,
          responsibles,
          disciplines
        });
        i = nextIndex;
        continue;
      }
    }

    currentText += lines[i] + '\n';
    i++;
  }

  flush();
  return segments;
}
